import React, {useEffect, useRef, useState} from 'react';
import {AutoComplete, Alert, Radio} from 'antd';
import {geoRequest, resolveLocation, searchArea, validPoint} from './service';
import {formatLocationAddress} from './addressLabel';
import {createSearchSession} from './providers/google';
import {useGeoConfig} from './config';
import GeoRetry from './GeoRetry';
import Attribution,{OSM_ATTRIBUTION} from './Attribution';
const BOGOTA={lat:4.6151959,lng:-74.1335376};
export default function AddressSearch({onSelect,center=BOGOTA}) {
  const {config}=useGeoConfig(); const provider=config.services.search;
  const [text,setText] = useState(''); const [options,setOptions] = useState([]); const [error,setError] = useState('');
  const [scope,setScope]=useState('national'); const [loading,setLoading]=useState(false);
  const [attribution,setAttribution]=useState(null);
  const [retryRevision,setRetryRevision]=useState(0),[failed,setFailed]=useState(false);
  const [wait,setWait]=useState(null),[requestError,setRequestError]=useState(null);
  const manualKey=useRef(null);
  const generation = useRef(0); const token = useRef(); const selected = useRef('');
  const pending=useRef(null); const currentCenter=useRef(center); currentCenter.current=center;
  // A national bias is a preference, not a viewport filter. Panning only repeats local searches.
  const local=provider==='megatrack' && scope==='local';
  const lat=local?center.lat:0, lng=local?center.lng:0;
  const queryKey=JSON.stringify([text,scope,provider,config.version,lat,lng]);
  useEffect(()=>{
    let alive = true; token.current=undefined;
    if(provider==='google' && config.execution.search==='browser') createSearchSession().then(t=>{if(alive) token.current=t;}).catch(()=>{});
    const requests = generation;
    return ()=>{alive=false; requests.current++;};
  },[provider,config.execution.search]);
  useEffect(()=>{
    const id=++generation.current;setOptions([]);setError('');setAttribution(null);setLoading(false);setFailed(false);setWait(null);setRequestError(null);
    if(text.trim().length<3 || text===selected.current) return;
    const controller=new AbortController();pending.current=controller;
    const timer=setTimeout(async()=>{
      setLoading(true);
      try {
        const input={address:text.trim(),sessionToken:token.current};
        if(provider==='megatrack') {
          if(local) input.bbox=searchArea({lat,lng});
          else {
            input.mode='national';
            if(validPoint(currentCenter.current)) input.bias={lon:currentCenter.current.lng,lat:currentCenter.current.lat};
          }
        }
        const {results,attribution:credit}=await geoRequest('search',input,{signal:controller.signal,autoRetry:manualKey.current!==queryKey,onWait:value=>{if(id===generation.current && !controller.signal.aborted)setWait(value);}});
        if(id===generation.current) {
          setOptions(results.map((place,index)=>{const label=formatLocationAddress(place);return {value:String(index),label:place.name && !label.toLocaleLowerCase().includes(place.name.toLocaleLowerCase())?`${place.name} · ${label}`:label,place};}));
          setAttribution(credit || results[0]?.attribution);
          setError(results.length ? '' : local?'No se encontraron resultados en esta área. Prueba en Toda Colombia o selecciona un punto.':'No se encontraron resultados. Agrega el municipio o selecciona un punto en el mapa.');
        }
      } catch(e) {if(id===generation.current && !controller.signal.aborted) {setOptions([]);setError(e.message);setFailed(true);setRequestError(e);}}
      finally {if(id===generation.current){setLoading(false);setWait(null);}}
    },provider==='megatrack'?650:400);
    const requests = generation;
    return ()=>{clearTimeout(timer);controller.abort();if(id===requests.current)requests.current++;};
  },[text,provider,config.version,local,scope,lat,lng,retryRevision,queryKey]);
  const invalidate=()=>{manualKey.current=null;setFailed(false);setWait(null);setRequestError(null);generation.current++;pending.current?.abort();setLoading(false);setOptions([]);setError('');};
  const choose=async(_,option)=>{
    invalidate();const id=generation.current;
    try {
      const place=await resolveLocation(option.place,token.current);
      if(id!==generation.current)return;
      selected.current=formatLocationAddress(place);setText(selected.current);setOptions([]);setError('');setAttribution(place.attribution);onSelect(place);
      if(provider==='google' && config.execution.search==='browser') token.current=await createSearchSession();
    } catch(e) {if(id===generation.current)setError(e.message);}
  };
  return <>
    {provider==='megatrack' && <div style={{marginBottom:12}}>
      <Radio.Group aria-label="Alcance de la búsqueda" value={scope} onChange={event=>{invalidate();selected.current='';setScope(event.target.value);}} style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:8}}>
        <Radio.Button value="national">Toda Colombia</Radio.Button><Radio.Button value="local">Área del mapa</Radio.Button>
      </Radio.Group>
      <p style={{margin:0}}>{local?'Busca en el área cercana al centro del mapa. Mueve el mapa para cambiar de zona.':'Busca direcciones y lugares en Colombia. La ubicación del mapa ayuda a priorizar los resultados cercanos.'}</p>
    </div>}
    <AutoComplete style={{width:'100%'}} value={text} options={options} onSearch={value=>{invalidate();selected.current='';setText(value);}}
      onSelect={choose} placeholder={local?'Buscar dirección, barrio o lugar en esta zona':'Buscar dirección, barrio o lugar en Colombia'} aria-label="Buscar dirección" allowClear onClear={()=>{invalidate();selected.current='';setText('');}} />
    {loading && <p role="status" style={{marginTop:8}}>Buscando ubicaciones…</p>}
    <Attribution attribution={attribution || (provider==='megatrack'?OSM_ATTRIBUTION:{text:'Google Maps',url:'https://maps.google.com'})} />
    {error && !failed && <Alert type="warning" message={error} showIcon style={{marginTop:8}} />}
    <GeoRetry label="Reintentar búsqueda" lookup={{wait,error:requestError,busy:loading,retry:()=>{manualKey.current=queryKey;setRetryRevision(value=>value+1);}}} />
  </>;
}
